import Flag from '@/components/Flag'
import DuoFunnelLayout from '@/components/v2/DuoFunnelLayout'
import DuoMascotPrompt from '@/components/v2/DuoMascotPrompt'
import DuoOptionRow from '@/components/v2/DuoOptionRow'
import DuoSignalBars from '@/components/v2/DuoSignalBars'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Form } from 'react-router-dom'
import type { DuoAnswer, DuoMotif, StepScreenProps } from './types'

/**
 * One question per screen, asked by the mascot in a speech bubble over a
 * vertical list of option rows. Picking a row only marks it: the sticky footer
 * CONTINUE stays disabled until something is selected and the user has to press
 * it, which is how every Duolingo register question behaves.
 *
 * The field name/value semantics mirror the shared `SingleChoiceQuestionPage`:
 * name is the English title, value is the answer's `value` key resolved through
 * `t()` or its English title.
 */
export default function SingleSelectStep({
  answers,
  interpolation,
  motif = 'emoji',
  onAnswered,
  step,
  subtitleKey,
  titleKey,
  totalSteps,
  withLogin,
}: StepScreenProps & {
  answers: DuoAnswer[]
  motif?: DuoMotif
  onAnswered?: (value: string) => void
  subtitleKey?: string
  titleKey: string
  withLogin?: boolean
}) {
  const [selectedValue, setSelectedValue] = useState<string | undefined>(
    undefined,
  )

  const { t } = useTranslation()

  const fieldName = t(titleKey, { lng: 'en', ...interpolation })

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value

    setSelectedValue(value)
    onAnswered?.(value)
  }

  return (
    <Form method="post">
      <DuoFunnelLayout
        footer={
          <button
            className="duo-button-primary"
            data-testid="button-continue"
            disabled={!selectedValue}
            type="submit"
          >
            {t('shared.continue')}
          </button>
        }
        step={step}
        totalSteps={totalSteps}
        withLogin={withLogin}
      >
        <DuoMascotPrompt
          subtitle={subtitleKey ? t(subtitleKey, interpolation) : undefined}
          text={t(titleKey, interpolation)}
        />

        <div className="mt-8 flex grow flex-col gap-3">
          {answers.map((answer, index) => {
            const value = answer.value
              ? t(answer.value)
              : t(answer.title, { lng: 'en', ...interpolation })
            const selected = selectedValue === value
            const subtitle = answer.subtitle
              ? t(answer.subtitle, interpolation)
              : undefined

            // Duolingo swaps the row anatomy per question type: ascending
            // signal bars for proficiency, a time/intensity split for the daily
            // goal, and a tall icon card for the two-way path choice.
            const isGoal = motif === 'goal'

            return (
              <DuoOptionRow
                className={motif === 'card' ? 'gap-4 px-5 py-6' : undefined}
                inputType="radio"
                key={answer.title}
                leading={
                  motif === 'signal-bars' ? (
                    <DuoSignalBars filled={index + 1} selected={selected} />
                  ) : isGoal ? undefined : (
                    <Flag
                      emoji={answer.emoji}
                      size={motif === 'card' ? 36 : 24}
                    />
                  )
                }
                name={fieldName}
                onChange={handleChange}
                selected={selected}
                subtitle={isGoal ? undefined : subtitle}
                title={t(answer.title, interpolation)}
                trailing={
                  isGoal ? (
                    <span className="text-duo-muted shrink-0 text-base font-medium">
                      {subtitle}
                    </span>
                  ) : undefined
                }
                value={value}
              />
            )
          })}
        </div>
      </DuoFunnelLayout>
    </Form>
  )
}
